import React, { useEffect } from 'react';
import cN from 'classnames';
import { IPlatformPageSection, PlatformPageSectionType } from '@/shared/types/platform-pages';
import { useRouter } from 'next/router';
import { scrollTo } from '@/shared/utils/utils';
import dynamic from 'next/dynamic';
import './index.less';

interface IProps {
  content: IPlatformPageSection;
  utmSource?: string;
  redirectUrl?: string;
}

const CalendarSection = dynamic(() => import('./content/CalendarSection'));
const Grid = dynamic(() => import('./content/Grid'));
const Image2x2GridSection = dynamic(() => import('./content/Image2x2GridSection'));
const InstructionalVideoSection = dynamic(() => import('./content/InstructionalVideoSection'));
const Pricing = dynamic(() => import('./content/Pricing'));
const PrimaryTextSection = dynamic(() => import('./content/PrimaryTextSection'));
const Reviews = dynamic(() => import('./content/Reviews'));
const SingleImageSection = dynamic(() => import('./content/SingleImageSection'));

export default function PlatformHero({ content, utmSource, redirectUrl }: IProps) {
  const router = useRouter();
  const sectionExtraClass = content.type.replaceAll('_', '-').toLowerCase();

  useEffect(() => {
    if (router?.query?.section) {
      scrollTo(router.query.section as string, 200);
    }
  }, [router?.query]);

  const renderContent = () => {
    switch (content.type) {
      case PlatformPageSectionType.TEXT_GRID:
        return (
          <>
            <PrimaryTextSection content={content} utmSource={utmSource} redirectUrl={redirectUrl} />
            <Grid content={content} />
          </>
        );
      case PlatformPageSectionType.CUSTOMER_REVIEWS:
        return (
          <>
            <PrimaryTextSection content={content} utmSource={utmSource} redirectUrl={redirectUrl} />
            <Reviews content={content} />
          </>
        );
      case PlatformPageSectionType.PRICING:
        return (
          <>
            <PrimaryTextSection content={content} utmSource={utmSource} redirectUrl={redirectUrl} />
            <Pricing content={content} redirectUrl={redirectUrl} />
          </>
        );
      case PlatformPageSectionType.TEXT_IMAGE:
        return (
          <>
            <PrimaryTextSection content={content} utmSource={utmSource} redirectUrl={redirectUrl} />
            <SingleImageSection content={content} />
          </>
        );
      case PlatformPageSectionType.INSTRUCTIONAL_VIDEOS:
        return (
          <>
            <PrimaryTextSection content={content} utmSource={utmSource} redirectUrl={redirectUrl} />
            <InstructionalVideoSection content={content} />
          </>
        );
      case PlatformPageSectionType.CALL_TO_ACTION:
        return <PrimaryTextSection content={content} utmSource={utmSource} redirectUrl={redirectUrl} />;
      case PlatformPageSectionType.IMAGE_2x2_GRID:
        return <Image2x2GridSection content={content} />;
      case PlatformPageSectionType.TEXT_CALENDAR:
        return (
          <>
            <PrimaryTextSection content={content} utmSource={utmSource} redirectUrl={redirectUrl} />
            <CalendarSection content={content} />
          </>
        );
      case PlatformPageSectionType.FULL_IMAGE:
        return <SingleImageSection content={content} />;
      default:
        return <div />;
    }
  };

  return (
    <div
      className={cN('platform-page-section', {
        [sectionExtraClass]: true,
      })}
      id={content?.id ?? ''}
      style={content?.background ? { background: content.background } : { background: content?.bgColor }}
    >
      <div className="section-wrapper">{renderContent()}</div>
    </div>
  );
}
